import { depositPayer, IBankTransaction } from './banking-api';
import { IRemit } from './remits-api';

/**
 * Reconcile's "Same payer" (FE-184): does this deposit come from the payer that sent this remit?
 *
 * Two keys, either one enough:
 *
 * - **The payer's id.** An insurer's ACH credit names its originator, `ORIG ID:1860813402` (or `ORIG ID=…`), and the 835
 *   names the same company in TRN03, "1" + its TIN. Compared on the last nine digits, the TIN, since one side often
 *   carries the "1" and the other does not. ERAs only: an EOB has no 835.
 * - **The payer's name.** The first word of the ACH's `ORIG CO NAME` inside the remit's payer name — the ACH name is cut
 *   short ("HUMANA GOVERNMEN", "PGBA TRICARE WR7"), so the whole of it rarely matches.
 *
 * Measured on production 2026-10-16, on 150 deposits already posted to their ERA: the id agrees on 107, the name on 81,
 * either on 111 — 74%. So it can hide the right remit about one time in four (clearing houses and payment vendors send
 * on a payer's behalf under their own id and name), and the page says so. Unchecked by default, as in the design.
 */
export const samePayer = (deposit: IBankTransaction, remit: IRemit): boolean => {
    const ours = tin(depositOriginId(deposit.name));
    const theirs = tin(remitOriginId(remit.x12));
    if (ours && theirs && ours === theirs) {
        return true;
    }
    const word = (depositPayer(deposit.name) || '').split(/\s+/)[0];
    return !!word && (remit.payerName || '').toUpperCase().includes(word.toUpperCase());
};

/** The ACH originator's company id, `ORIG ID:1860813402` or `ORIG ID=1860813402`. */
export const depositOriginId = (name: string | undefined): string | undefined =>
    (/ORIG ID[:=]\s*([0-9A-Z]+)/i.exec(name || '') || [])[1];

/** TRN03 of an 835: the payer's originating company id. */
export const remitOriginId = (x12: string | undefined): string | undefined =>
    (/TRN\*1\*[^*~]+\*([^*~]+)/.exec(x12 || '') || [])[1]?.trim() || undefined;

/** The last nine digits — a TIN — or nothing when there are fewer. */
const tin = (id: string | undefined): string | undefined => {
    const digits = (id || '').replace(/\D/g, '');
    return digits.length >= 9 ? digits.slice(-9) : undefined;
};
